import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from './api';
import MathText from './MathText';
import './QuestionMappingModal.css';

// Eşlenmemiş soruları (hafta + ÖÇ) eşleştirme modalı.
// ÖÇ seçilir; PÇ otomatik olarak outcome_mappings'ten türetilir (backend).
function QuestionMappingModal({ courseId, questions, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [learningOutcomes, setLearningOutcomes] = useState([]); // [{id,label,name,program_outcomes:[{id,label,name}]}]
  const [weeklyTopics, setWeeklyTopics] = useState([]); // [{week,topic}]
  // Tabloyu açılışta dondur: kaydedilen soru anında listeden düşmesin
  const [tableQuestions] = useState(questions);
  // satır durumu: { [questionId]: { week_number, selectedLoIds:Set, saving, saved } }
  const [rows, setRows] = useState({});

  const loMap = useMemo(() => {
    const m = {};
    learningOutcomes.forEach((lo) => { m[lo.id] = lo; });
    return m;
  }, [learningOutcomes]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiFetch(`/instructor/courses/${courseId}/outcome-options`);
        const data = await res.json();
        if (!active) return;
        if (data.success) {
          setLearningOutcomes(data.learning_outcomes || []);
          setWeeklyTopics(data.weekly_topics || []);
        } else {
          setError(data.message || 'Seçenekler yüklenemedi');
        }
      } catch {
        if (active) setError('Bağlantı hatası');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [courseId]);

  // Soru listesinden başlangıç satır durumunu kur (açılıştaki dondurulmuş liste)
  useEffect(() => {
    const init = {};
    tableQuestions.forEach((q) => {
      init[q.id] = {
        week_number: q.week_number ?? '',
        selectedLoIds: new Set((q.learning_outcomes || []).map((lo) => lo.id)),
        saving: false,
        saved: false,
      };
    });
    setRows(init);
  }, [tableQuestions]);

  const setRow = (qid, patch) => {
    setRows((prev) => ({ ...prev, [qid]: { ...prev[qid], ...patch } }));
  };

  const toggleLo = (qid, loId) => {
    setRows((prev) => {
      const row = prev[qid];
      const next = new Set(row.selectedLoIds);
      if (next.has(loId)) next.delete(loId); else next.add(loId);
      return { ...prev, [qid]: { ...row, selectedLoIds: next, saved: false } };
    });
  };

  // Seçili ÖÇ'lerden türeyen PÇ'ler (önizleme için, client-side)
  const derivedPOs = (selectedLoIds) => {
    const seen = new Set();
    const list = [];
    selectedLoIds.forEach((loId) => {
      const lo = loMap[loId];
      (lo?.program_outcomes || []).forEach((po) => {
        if (!seen.has(po.id)) { seen.add(po.id); list.push(po); }
      });
    });
    return list;
  };

  const handleSave = async (qid) => {
    const row = rows[qid];
    if (!row) return;
    const weekVal = row.week_number === '' ? null : Number(row.week_number);
    if (weekVal == null) { setRow(qid, { error: 'Hafta seçin' }); return; }
    if (row.selectedLoIds.size === 0) { setRow(qid, { error: 'En az bir ÖÇ seçin' }); return; }
    setRow(qid, { saving: true, error: '' });
    try {
      const res = await apiFetch(`/instructor/courses/${courseId}/questions/${qid}/mapping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          week_number: weekVal,
          learning_outcome_ids: Array.from(row.selectedLoIds),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setRow(qid, { saving: false, saved: true, error: '' });
        if (onSaved) {
          onSaved(qid, {
            week_number: data.week_number,
            learning_outcomes: data.learning_outcomes || [],
            program_outcomes: data.program_outcomes || [],
          });
        }
      } else {
        setRow(qid, { saving: false, error: data.message || 'Kaydedilemedi' });
      }
    } catch {
      setRow(qid, { saving: false, error: 'Bağlantı hatası' });
    }
  };

  return (
    <div className="qmm-overlay" onClick={onClose}>
      <div className="qmm-content" onClick={(e) => e.stopPropagation()}>
        <div className="qmm-header">
          <h3>Soru Eşleştirme — Hafta & ÖÇ</h3>
          <button className="qmm-close" onClick={onClose}>×</button>
        </div>

        <p className="qmm-hint">
          Her soru için <strong>hafta</strong> ve <strong>öğrenme çıktısı (ÖÇ)</strong> seçin.
          Program çıktıları (PÇ) seçtiğiniz ÖÇ'lerden otomatik gelir. Eşlenen sorular sınavlarda sorulabilir.
        </p>

        {loading ? (
          <div className="qmm-state">Yükleniyor...</div>
        ) : error ? (
          <div className="qmm-state qmm-error">{error}</div>
        ) : tableQuestions.length === 0 ? (
          <div className="qmm-state">Eşlenmemiş soru yok.</div>
        ) : (
          <div className="qmm-table-wrap">
            <table className="qmm-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>SORU</th>
                  <th style={{ width: 130 }}>HAFTA</th>
                  <th style={{ width: 200 }}>EŞLEŞEN ÖÇ / PÇ</th>
                  <th style={{ width: 360 }}>ÖÇ SEÇ / DEĞİŞTİR</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {tableQuestions.map((q, index) => {
                  const row = rows[q.id] || { week_number: '', selectedLoIds: new Set() };
                  const pos = derivedPOs(row.selectedLoIds);
                  return (
                    <tr key={q.id} className={row.saved ? 'qmm-row-saved' : ''}>
                      <td className="qmm-td-no">{index + 1}</td>
                      <td className="qmm-td-text"><MathText text={q.question_text || ''} /></td>
                      <td>
                        <select
                          className="qmm-week-select"
                          value={row.week_number}
                          onChange={(e) => setRow(q.id, { week_number: e.target.value, saved: false })}
                        >
                          <option value="">Hafta seçin…</option>
                          {weeklyTopics.map((wt) => (
                            <option key={wt.week} value={wt.week} title={wt.topic}>
                              {wt.week}. Hafta{wt.topic ? ` — ${wt.topic.substring(0, 24)}${wt.topic.length > 24 ? '…' : ''}` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {row.selectedLoIds.size === 0 ? (
                          <span className="qmm-dash">—</span>
                        ) : (
                          <div className="qmm-badges">
                            {Array.from(row.selectedLoIds).map((loId) => (
                              <span key={`lo-${loId}`} className="qmm-badge qmm-badge-lo">{loMap[loId]?.label || '?'}</span>
                            ))}
                            {pos.map((po) => (
                              <span key={`po-${po.id}`} className="qmm-badge qmm-badge-po" title={po.name}>{po.label}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="qmm-lo-list">
                          {learningOutcomes.length === 0 ? (
                            <span className="qmm-dash">Bu derste tanımlı ÖÇ yok</span>
                          ) : learningOutcomes.map((lo) => (
                            <label key={lo.id} className="qmm-lo-item" title={lo.name}>
                              <input
                                type="checkbox"
                                checked={row.selectedLoIds.has(lo.id)}
                                onChange={() => toggleLo(q.id, lo.id)}
                              />
                              <span className="qmm-lo-label">{lo.label}</span>
                              <span className="qmm-lo-name">{lo.name}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td>
                        <button
                          className="qmm-save-btn"
                          disabled={row.saving}
                          onClick={() => handleSave(q.id)}
                        >
                          {row.saving ? '…' : row.saved ? '✓ Kaydedildi' : 'Kaydet'}
                        </button>
                        {row.error && <div className="qmm-row-error">{row.error}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="qmm-footer">
          <button className="qmm-footer-btn" onClick={onClose}>Kapat</button>
        </div>
      </div>
    </div>
  );
}

export default QuestionMappingModal;
